import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Stack,
  Typography,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro'
import {
  AUDIENCE_ELIGIBILITY_SAMPLE_SIZE,
  AUDIENCE_EXCLUSION_REASON_LABEL,
  AUDIENCE_EXCLUSION_REASONS,
  type AudienceEligibilityPreview,
  type AudienceExclusionReason,
  type AudiencePreviewRow,
} from '@otw/models/leads/marketingCampaign'
import React, { useMemo } from 'react'
import { formatNumber } from '../../util/customerUtils'

interface AudienceEligibilitySummaryProps {
  preview: AudienceEligibilityPreview
}

const AudienceEligibilitySummary: React.FC<AudienceEligibilitySummaryProps> = ({
  preview,
}) => {
  const reasonLines = AUDIENCE_EXCLUSION_REASONS.filter(
    (reason) => preview.excludedByReason[reason] > 0,
  )

  const eligibleColumns = useMemo<GridColDef<AudiencePreviewRow>[]>(
    () => [
      { field: 'name', headerName: 'Contact', flex: 1, minWidth: 140 },
      { field: 'email', headerName: 'Email', flex: 1, minWidth: 160 },
    ],
    [],
  )

  const excludedColumns = useMemo<GridColDef<AudiencePreviewRow>[]>(
    () => [
      { field: 'name', headerName: 'Contact', flex: 1, minWidth: 140 },
      { field: 'email', headerName: 'Email', flex: 1, minWidth: 140 },
      {
        field: 'reasons',
        headerName: 'Why excluded',
        flex: 1.2,
        minWidth: 180,
        valueGetter: (_value, row) =>
          row.reasons
            .map((reason: AudienceExclusionReason) => AUDIENCE_EXCLUSION_REASON_LABEL[reason])
            .join(', '),
      },
    ],
    [],
  )

  const eligibleTruncated = preview.eligibleCount > AUDIENCE_ELIGIBILITY_SAMPLE_SIZE
  const excludedTruncated = preview.excludedCount > AUDIENCE_ELIGIBILITY_SAMPLE_SIZE

  return (
    <Stack spacing={1}>
      <Typography variant="body2">
        Matching records: {formatNumber(preview.matchingCount)}
      </Typography>
      <Typography variant="body2">
        Eligible recipients: {formatNumber(preview.eligibleCount)}
      </Typography>
      <Typography variant="body2">
        Excluded: {formatNumber(preview.excludedCount)}
      </Typography>
      {reasonLines.length > 0 ? (
        <Stack spacing={0.25} sx={{ pl: 1.5 }}>
          {reasonLines.map((reason) => (
            <Typography key={reason} variant="caption" color="text.secondary">
              {AUDIENCE_EXCLUSION_REASON_LABEL[reason]}:{' '}
              {formatNumber(preview.excludedByReason[reason])}
            </Typography>
          ))}
        </Stack>
      ) : null}
      <Typography variant="caption" color="text.secondary">
        Unsubscribe and suppress always win over the audience filter. Duplicate emails keep
        the most recently active matching contact. Recipients are re-evaluated at send.
      </Typography>
      <Accordion disableGutters elevation={0} sx={{ '&:before': { display: 'none' } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">Preview eligible</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {preview.eligibleSample.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No eligible recipients with the current filters.
            </Typography>
          ) : (
            <>
              {eligibleTruncated ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Showing first {formatNumber(AUDIENCE_ELIGIBILITY_SAMPLE_SIZE)} of{' '}
                  {formatNumber(preview.eligibleCount)}
                </Typography>
              ) : null}
              <DataGridPro
                rows={preview.eligibleSample}
                columns={eligibleColumns}
                autoHeight
                hideFooter
                disableRowSelectionOnClick
                sx={{ border: 0 }}
              />
            </>
          )}
        </AccordionDetails>
      </Accordion>
      <Accordion disableGutters elevation={0} sx={{ '&:before': { display: 'none' } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">Preview excluded</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {preview.excludedSample.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No matching contacts are excluded.
            </Typography>
          ) : (
            <>
              {excludedTruncated ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Showing first {formatNumber(AUDIENCE_ELIGIBILITY_SAMPLE_SIZE)} of{' '}
                  {formatNumber(preview.excludedCount)}
                </Typography>
              ) : null}
              <DataGridPro
                rows={preview.excludedSample}
                columns={excludedColumns}
                autoHeight
                hideFooter
                disableRowSelectionOnClick
                sx={{ border: 0 }}
              />
            </>
          )}
        </AccordionDetails>
      </Accordion>
    </Stack>
  )
}

export default AudienceEligibilitySummary
